import { FolderAssetType } from '../model';

// use processors to append any info to AdsStudioODataError instance based on errorCode,
// for example:
//    uxMessage: message to show on UI
//    actionable: if use could correct this error
//    getUXMessage(): function to get message shown on UI, but could pass in additiona parameters
//    ...

export const renameFolderApiErrorProcessor = (errorCode) => {
  const msgs = {
    InvalidFolderId: {
      uxMessage: _TL_("This folder doesn't exist because you may have already deleted it. Please refresh the page to confirm."),
      actionable: false,
    },
    DuplicateFolderName: {
      uxMessage: _TL_('This folder name already exists. Please enter another one.'),
      actionable: true,
    },
    InvalidFolderName: {
      uxMessage: _TL_("You've entered one or more invalid characters in the fields. Please correct them and try again."),
      actionable: true,
    },
    genericError: {
      uxMessage: _TL_("Your folder couldn't be renamed. Please refresh the page and try again."),
      actionable: false,
    },
  };

  // actionable:
  //    true - ask user to do correct the error
  //    false - user can't do any action, just show notification
  return msgs[errorCode] || msgs.genericError;
};

export const deleteFolderApiErrorProcessor = (errorCode /* {folderId, folderName} */) => {
  const msgs = {
    InvalidFolderId: {
      uxMessage: _TL_("This folder doesn't exist because you may have already deleted it. Please refresh the page to confirm."),
      ignorable: true,
      shortMsg: _TL_('Error'),
    },

    FolderHasAssets: {
      uxMessage: _TL_("Your folder couldn't be deleted because it contains assets or subfolders. Please remove these and try again."),
      shortMsg: _TL_("Folder isn't empty"),
      ignorable: true,
    },

    genericError: {
      uxMessage: _TL_("Your folder couldn't be deleted. Please refresh the page and try again."),
      shortMsg: _TL_('Error'),
    },
  };
  msgs.InvalidId = msgs.InvalidFolderId;
  msgs.FolderHasSubFolders = msgs.FolderHasAssets;

  // actionable:
  //    true - ask user to do correct the error
  //    false - user can't do any action, just show notification
  return msgs[errorCode] || msgs.genericError;
};

export const bulkMoveApiErrorProcessor = (errorCode, { folderCount, assetCount }) => {
  const msgs = {
    // DestFolderId is invalid
    InvalidFolderId: {
      getUXMessage: i18n => i18n.getString(
        _TL_("({{folderCount}} folders, {{assetCount}} assets)) couldn't be moved because you may have already deleted the folder. Please refresh the page and try a different folder."),
        { folderCount, assetCount }
      ),
      actionable: false,
    },

    genericError: {
      getUXMessage: i18n => i18n.getString(
        _TL_("({{folderCount}} folders, {{assetCount}} assets) couldn't be moved. Please refresh the page and try again."),
        { folderCount, assetCount }
      ),
    },
  };

  // actionable:
  //    true - ask user to do correct the error
  //    false - user can't do any action, just show notification
  return msgs[errorCode] || msgs.genericError;
};

// error for a specific item in the batch response
export const bulkMoveApiItemErrorProcessor = (errorCode) => {
  const msgs = {
    InvalidFolderId: {
      uxMessage: _TL_("This folder doesn't exist because you may have already deleted it. Please refresh the page to confirm."),
      shortMsg: _TL_("Folder doesn't exist"),
      availableActions: {},
    },
    AssetIdInvalid: {
      uxMessage: _TL_("This asset doesn't exist because you may have already deleted it. Please refresh the page to confirm."),
      shortMsg: _TL_("Asset doesn't exist"),
      availableActions: {},
    },
    DuplicateAssetsUnderFolder: {
      uxMessage: _TL_('Your asset already exists in the folder. Please note that you can add the same asset to different folders, but you cannot have duplicate assets in the same folder.'),
      shortMsg: _TL_('Duplicate asset'),
      availableActions: {},
    },
    DuplicateImage: {
      uxMessage: _TL_('Your image already exists in the folder. Please note that you can add the same image to different folders, but you cannot have duplicate images in the same folder.'),
      shortMsg: _TL_('Duplicate image'),
      availableActions: {},
    },
    DuplicateFolderName: {
      uxMessage: _TL_('You cannot have duplicate subfolder names in the same folder. Please rename your subfolder and try again.'),
      shortMsg: _TL_('Duplicate subfolder name'),
      availableActions: {},
    },
    FolderCanNotMoveToTarget: {
      uxMessage: _TL_('You cannot move a folder into itself or one of its subfolders. Please select a different folder and try again.'),
      shortMsg: _TL_('Wrong target folder'),
      availableActions: {},
    },

    genericError: {
      uxMessage: _TL_("Your assets couldn't be moved. Please refresh the page and try again."),
      shortMsg: _TL_('Error'),
      availableActions: {},
    },
  };

  // actionable:
  //    true - ask user to do correct the error
  //    false - user can't do any action, just show notification
  return msgs[errorCode] || msgs.genericError;
};

export const deleteAssetApiErrorProcessor = (errorCode, { assetType }) => {
  const msgs = {
    AssetIsUsed: {
      uxMessage: assetType === FolderAssetType.Video
        ? _TL_("You can't delete this video because it's being used by other campaigns, ads, or video extensions.")
        : _TL_("You can't delete this image because it's being used by other campaigns, ads, or image extensions."),
      availableActions: {
        gotoAds: true,
        // others: true,
      },
    },
    // ImageAdExtensionPilotNotEnabledForCustomer
    // AssetDoesNotBelongToAccount
    // AssetIdInvalid
    // AssetIsAlreadyDeleted
    // CamAssetCannotBeDeleted --Asset of this Type ( CAM ) can not be deleted
    genericError: {
      uxMessage: _TL_('Failed to delete image. Please try again or contact support.'),
      availableActions: {},
    },
  };
  return msgs[errorCode] || msgs.genericError;
};

export const createFolderApiErrorProcessor = (errorCode) => {
  const msgs = {
    DuplicateEntity: {
      getUXMessage: i18n => i18n.getString(_TL_('This folder name already exists. Please enter another one.')),
      actionable: true,
    },
    InvalidFolderName: {
      getUXMessage: i18n => i18n.getString(_TL_("You've entered one or more invalid characters in the fields. Please correct them and try again.")),
      actionable: true,
    },
    FolderLimitExceededPerAccount: {
      getUXMessage: (i18n, getFolderLimit) => (i18n.getString(_TL_('Failed to create one or more folders because your account has exceeded the {{value}}-folder limit. Please remove folders and try again.'), getFolderLimit())
      ),
      actionable: false,
    },
    genericError: {
      getUXMessage: i18n => i18n.getString(_TL_('Failed to create folder. Please try again.')),
      actionable: false,
    },
  };
  return msgs[errorCode] || msgs.genericError;
};

// ## rename asset
// AssetIsNull
// AssetDoesNotBelongToAccount
// AssetIsAlreadyDeleted

//
// ## bulk delete asset
export const bulkDeleteAssetsApiItemErrorProcessor = (errorCode, { assetType }) => {
  const msgs = {
    // AssetIdInvalid
    // AssetIsAlreadyDeleted
    CampaignServiceMediaIdInvalid: {
      uxMessage: _TL_("This asset doesn't exist because you may have already deleted it. Please refresh the page to confirm."),
      shortMsg: _TL_("Asset doesn't exist"),
      availableActions: {},
    },
    // // AssetDoesNotBelongToAccount
    // CampaignServiceMediaNotFoundInAccount: {
    //   uxMessage: _TL_('Asset does not belong to account'),
    //   availableActions: {},
    // },
    // // CamAssetCannotBeDeleted
    // CampaignServiceMediaTypeInvalidForOperation: {
    //   uxMessage: _TL_('Asset of this Type ( CAM ) cannot be deleted'),
    //   availableActions: {},
    // },
    // AssetIsUsed
    CampaignServiceMediaIsAssociated: {
      uxMessage: assetType === FolderAssetType.Video
        ? _TL_("You can't delete this video because it's being used by other campaigns, ads, or video extensions. Delete these associations and try again.")
        : _TL_("You can't delete this image because it's being used by other campaigns, ads, or image extensions. Delete these associations and try again."),
      shortMsg: _TL_('Asset is currently in use '),
      availableActions: {
        gotoAds: true,
        // others: true,
      },
    },
    // // AccountIdInvalid
    // CampaignServiceInvalidAccountId: {
    //   uxMessage: _TL_('account id is invalid'),
    //   availableActions: {},
    // },
    // // DuplicateId
    // DuplicateId: {
    //   uxMessage: _TL_('There are dulpicate ids from input'),
    //   availableActions: {},
    // },
    genericError: {
      uxMessage: _TL_("Your assets couldn't be deleted. Please refresh the page and try again."),
      shortMsg: _TL_('Error'),
      availableActions: {},
    },
  };
  return msgs[errorCode] || msgs.genericError;
};

export const bulkDeleteAssetsApiErrorProcessor = (errorCode) => {
  const msgs = {
    genericError: {
      uxMessage: _TL_("Your assets couldn't be deleted. Please refresh the page and try again."),
      shortMsg: _TL_('Error'),
      availableActions: {},
    },
  };
  return msgs[errorCode] || msgs.genericError;
};

export const generateImagesWithAIAPIErrorProcessor = (errorCode, params) => {
  const reqCountErr = _TL_("Sorry, our servers are busy and we're unable to process your request. We're working to resolve this issue. Please try again later.");

  const msgs = {
    GenerateImagesPromptLengthInvalid: {
      uxMessage: _TL_('Your image description is either too long or too short. 256 characters maximum.'),
    },
    GenerateImagesRequestCountLimitExceededPerAccount: {
      uxMessage: reqCountErr,
    },
    GenerateImagesRequestCountLimitExceeded: {
      uxMessage: reqCountErr,
    },
    GenerateImagesPromptContentInvalid: { // when AIGC told MT the input prompt is invalid
      uxMessage: _TL_('Something went wrong with your request and we\'re unable to process it at this time.'),
    },

    // use general message for this one
    // GenerateImagesAIGCNoRecommendation: when AIGC failed to create task, failed to generate image, but it does not specify the reason.

    GenerateImagesAIGCNoRecommendationWithBrand: {
      brandNameDetected: true,
      brandNames: (params && params.errorMessage) || '',
    },

    genericError: {
      uxMessage: _TL_('We\'ve encountered an error and failed to generate images.'),
    },
  };
  return msgs[errorCode] || msgs.genericError;
};

export const getAIGeneratedImageAPIErrorProcessor = (errorCode) => {
  const msgs = {
    AI_Generation_Failed: {
      shouldRetry: false,
      uxMessage: _TL_('Something went wrong with your request and we\'re unable to process it at this time. We apologize for the inconvenience. Please try again later.'),
    },
    genericError: {
      shouldRetry: true,
      uxMessage: _TL_('We\'ve encountered an error and failed to generate images.'),
    },
  };
  return msgs[errorCode] || msgs.genericError;
};

// todo: handle error codes returned from MT (what error codes are returned from MT?)
// todo: scrub error message text
const generalBackgroundAPIErrorMessages = {
  GenerateImagesPromptContentInvalid: { // when AIGC told MT the input prompt is invalid
    uxMessage: _TL_('Something went wrong with your request and we\'re unable to process it at this time.'),
  },
  GenerationBackgroundFailed: {
    shouldRetry: false,
    uxMessage: _TL_('Something went wrong with your request and we\'re unable to process it at this time. We apologize for the inconvenience. Please try again later.'),
  },
  GenerateImagesAIGCInvalidImageSize: {
    uxMessage: _TL_('We’re unable to generate the image because the image size does not meet the minimum required dimensions.'),
  },
  GenerateImagesAIGCInvalidImageContent: {
    uxMessage: _TL_('We’re unable to generate the image because the image you provided may violate our policies and guidelines. To learn more, see '),
    availableActions: {
      gotoDisallowedContent: true,
    },
  },
  GenerateImageAIGCInvalidUserPrompt: {
    uxMessage: _TL_('We’re unable to generate the image because the prompt you provided may violate our policies and guidelines. To learn more, see '),
    availableActions: {
      gotoDisallowedContent: true,
    },
  },
  GenerateImageAIGCInvalidGeneratedImage: {
    uxMessage: _TL_('We’re unable to generate the image because it may violate our policies and guidelines. To learn more, see '),
    availableActions: {
      gotoDisallowedContent: true,
    },
  },
};

export const getRemoveImageBackgroundAPIErrorProcessor = (errorCode) => {
  const msgs = {
    ...generalBackgroundAPIErrorMessages,
    genericError: {
      shouldRetry: true,
      uxMessage: _TL_('We’ve encountered an error removing the image’s background. Please try again.'),
    },
  };
  return msgs[errorCode] || msgs.genericError;
};

export const getGenerateImageBackgroundAPIErrorProcessor = (errorCode) => {
  const msgs = {
    ...generalBackgroundAPIErrorMessages,
    genericError: {
      shouldRetry: true,
      uxMessage: _TL_('We’ve encountered an error generating the image’s background. Please try again.'),
    },
  };
  return msgs[errorCode] || msgs.genericError;
};
